import React, { useCallback, useEffect, useState } from "react";
import {
  adminFetchPartnerInvoices,
  adminSendPartnerInvoice,
  adminMarkPartnerInvoicePaid,
  adminDeletePartnerInvoiceDraft,
} from "../../core/axios/endpoints";
import ConfirmModal from "../../components/confirm-modal";
import PartnerInvoiceDetailModal from "../../components/partner-invoice-detail-modal";

const fmtEUR = (n) =>
  `€ ${Number(n ?? 0).toLocaleString("nl-NL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString("nl-NL") : "-");

const STATUS_LABELS = {
  draft: { label: "Concept", color: "bg-slate-100 text-slate-700" },
  sent: { label: "Verstuurd", color: "bg-sky-100 text-sky-800" },
  paid: { label: "Betaald", color: "bg-emerald-100 text-emerald-800" },
  overdue: { label: "Achterstallig", color: "bg-amber-100 text-amber-800" },
  cancelled: { label: "Geannuleerd", color: "bg-rose-100 text-rose-800" },
};

const TYPE_LABELS = {
  invoice: { label: "Factuur", color: "bg-gray-100 text-gray-700" },
  credit_note: { label: "Creditnota", color: "bg-rose-50 text-rose-800" },
};

const Badge = ({ map, value }) => {
  const cfg = map[value] ?? { label: value, color: "bg-gray-100 text-gray-700" };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${cfg.color}`}
    >
      {cfg.label}
    </span>
  );
};

const AdminPartnerFacturenPage = () => {
  const [data, setData] = useState({ docs: [], pagination: {}, totals: {} });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [busyId, setBusyId] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const [detailId, setDetailId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [markPaidRow, setMarkPaidRow] = useState(null);
  const [markPaidDate, setMarkPaidDate] = useState(
    new Date().toISOString().slice(0, 10),
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const body = await adminFetchPartnerInvoices({
        page,
        limit: 25,
        status,
        type,
        search,
      });
      const payload = body?.data ?? body;
      setData({
        docs: payload?.docs ?? [],
        pagination: payload?.pagination ?? {},
        totals: payload?.totals ?? {},
      });
    } catch (e) {
      console.error("Partner-facturen laden mislukt:", e);
    } finally {
      setLoading(false);
    }
  }, [page, status, type, search]);

  useEffect(() => {
    refresh();
  }, [refresh, reloadKey]);

  const onSend = async (id) => {
    setBusyId(id);
    try {
      await adminSendPartnerInvoice(id, { resend: true });
      setReloadKey((k) => k + 1);
    } catch (e) {
      alert(e?.response?.data?.error ?? "Versturen mislukt.");
    } finally {
      setBusyId(null);
    }
  };

  const onTogglePaid = async (row) => {
    if (row.status === "paid") {
      // Undo direct (geen datum-prompt nodig).
      setBusyId(row._id);
      try {
        await adminMarkPartnerInvoicePaid(row._id, { undo: true });
        setReloadKey((k) => k + 1);
      } catch (e) {
        alert(e?.response?.data?.error ?? "Status wijzigen mislukt.");
      } finally {
        setBusyId(null);
      }
      return;
    }
    // Markeer betaald: open mini-modal voor betaaldatum (default vandaag).
    setMarkPaidDate(new Date().toISOString().slice(0, 10));
    setMarkPaidRow(row);
  };

  const confirmMarkPaid = async () => {
    if (!markPaidRow) return;
    setBusyId(markPaidRow._id);
    try {
      // Stuur datum als ISO-string; server slaat 'm op als Date.
      // Mensen vullen meestal alleen dag in - zet 'm op 12:00 UTC zodat
      // tijdzone-shifts (NL = UTC+2 in DST) niet alsnog gisteren tonen.
      const paidAt = new Date(`${markPaidDate}T12:00:00.000Z`).toISOString();
      await adminMarkPartnerInvoicePaid(markPaidRow._id, { paidAt });
      setMarkPaidRow(null);
      setReloadKey((k) => k + 1);
    } catch (e) {
      alert(e?.response?.data?.error ?? "Markeren mislukt.");
    } finally {
      setBusyId(null);
    }
  };

  const onDeleteDraft = async () => {
    if (!deleteId) return;
    setBusyId(deleteId);
    try {
      await adminDeletePartnerInvoiceDraft(deleteId);
      setDeleteId(null);
      setReloadKey((k) => k + 1);
    } catch (e) {
      alert(e?.response?.data?.error ?? "Verwijderen mislukt.");
    } finally {
      setBusyId(null);
    }
  };

  const t = data.totals ?? {};

  return (
    <div className="page-wrapper px-6 pt-24 pb-12">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold tracking-tight text-gray-900">
            Partner-facturen
          </h2>
          <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
            <span>
              <span className="font-medium text-gray-900">{t.count ?? 0}</span>{" "}
              facturen
            </span>
            <span aria-hidden>·</span>
            <span>
              Open{" "}
              <span className="font-medium text-amber-700 tabular-nums">
                {fmtEUR(t.openIncl)}
              </span>
            </span>
            <span aria-hidden>·</span>
            <span>
              Betaald{" "}
              <span className="font-medium text-emerald-700 tabular-nums">
                {fmtEUR(t.paidIncl)}
              </span>
            </span>
            {t.creditedIncl ? (
              <>
                <span aria-hidden>·</span>
                <span>
                  Gecrediteerd{" "}
                  <span className="font-medium text-rose-700 tabular-nums">
                    {fmtEUR(t.creditedIncl)}
                  </span>
                </span>
              </>
            ) : null}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          className="rounded-md border border-gray-200 px-2.5 py-1.5 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
        >
          <option value="">Alle statussen</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>
              {v.label}
            </option>
          ))}
        </select>
        <select
          value={type}
          onChange={(e) => {
            setType(e.target.value);
            setPage(1);
          }}
          className="rounded-md border border-gray-200 px-2.5 py-1.5 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
        >
          <option value="">Facturen + creditnota&apos;s</option>
          <option value="invoice">Alleen facturen</option>
          <option value="credit_note">Alleen creditnota&apos;s</option>
        </select>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Zoek op nummer, kenmerk, naam, email…"
          className="flex-1 min-w-[220px] rounded-md border border-gray-200 px-3 py-1.5 text-sm focus:border-emerald-500 focus:outline-none"
        />
      </div>

      {/* Tabel */}
      <div className="overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-gray-200">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
            <tr>
              <th className="px-4 py-2.5">Nummer</th>
              <th className="px-4 py-2.5">Partner</th>
              <th className="px-4 py-2.5">Type</th>
              <th className="px-4 py-2.5">Datum</th>
              <th className="px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5 text-right">Totaal</th>
              <th className="px-4 py-2.5 text-right">Acties</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-gray-500">
                  Laden…
                </td>
              </tr>
            ) : data.docs.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-gray-500">
                  Geen partner-facturen gevonden.
                </td>
              </tr>
            ) : (
              data.docs.map((row) => (
                <tr
                  key={row._id}
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => setDetailId(row._id)}
                >
                  <td className="px-4 py-2.5 font-mono text-xs text-gray-900">
                    {row.invoiceNumber}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="font-medium text-gray-900">
                      {row.partnerUserId?.bedrijfsnaam ??
                        row.partnerUserId?.naam ??
                        "-"}
                    </div>
                    <div className="text-xs text-gray-500">{row.billingEmail}</div>
                  </td>
                  <td className="px-4 py-2.5">
                    <Badge map={TYPE_LABELS} value={row.type} />
                  </td>
                  <td className="px-4 py-2.5 text-gray-700">
                    {fmtDate(row.invoiceDate)}
                  </td>
                  <td className="px-4 py-2.5">
                    <Badge map={STATUS_LABELS} value={row.status} />
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums font-medium text-gray-900">
                    {fmtEUR(row.totalIncl)}
                  </td>
                  <td
                    className="px-4 py-2.5 text-right"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex flex-wrap justify-end gap-1.5">
                      {row.status === "draft" ? (
                        <>
                          <button
                            type="button"
                            onClick={() => onSend(row._id)}
                            disabled={busyId === row._id}
                            className="rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                          >
                            Verstuur
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeleteId(row._id)}
                            disabled={busyId === row._id}
                            className="rounded-md border border-rose-300 px-2.5 py-1 text-xs font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                          >
                            Verwijder
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          onClick={() => onTogglePaid(row)}
                          disabled={busyId === row._id || row.status === "cancelled"}
                          className={
                            row.status === "paid"
                              ? "rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                              : "rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-medium text-white shadow-sm hover:bg-emerald-700 disabled:opacity-50"
                          }
                        >
                          {row.status === "paid"
                            ? "Markeer onbetaald"
                            : "Markeer betaald"}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {data.pagination?.totalPages > 1 ? (
        <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
          <span>
            Pagina {data.pagination.page} van {data.pagination.totalPages}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 disabled:opacity-50"
            >
              Vorige
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= (data.pagination.totalPages ?? 1) || loading}
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 disabled:opacity-50"
            >
              Volgende
            </button>
          </div>
        </div>
      ) : null}

      {detailId ? (
        <PartnerInvoiceDetailModal
          isOpen={!!detailId}
          invoiceId={detailId}
          onClose={() => setDetailId(null)}
          onChanged={() => {
            setReloadKey((k) => k + 1);
          }}
        />
      ) : null}

      <ConfirmModal
        isOpen={!!deleteId}
        variant="danger"
        title="Draft-factuur verwijderen?"
        message="De factuur wordt verwijderd en de gekoppelde aanvragen komen terug op 'nog niet gefactureerd'. Dit kan alleen voor concepten."
        confirmLabel="Verwijder draft"
        cancelLabel="Annuleren"
        busy={!!busyId}
        onConfirm={onDeleteDraft}
        onCancel={() => setDeleteId(null)}
      />

      {/* Markeer-betaald modal met datum-input. Default = vandaag, maar je
          kunt de echte datum invullen als de betaling al eerder binnenkwam. */}
      {markPaidRow ? (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl ring-1 ring-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              Markeer als betaald
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Factuur <span className="font-mono">{markPaidRow.invoiceNumber}</span> &middot;{" "}
              {fmtEUR(markPaidRow.totalIncl)}
            </p>
            <label className="mt-5 block text-sm">
              <span className="font-medium text-gray-900">
                Datum waarop betaald is
              </span>
              <input
                type="date"
                value={markPaidDate}
                max={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setMarkPaidDate(e.target.value)}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                disabled={!!busyId}
              />
              <span className="mt-1 block text-xs text-gray-500">
                Standaard vandaag &mdash; pas aan als de betaling eerder binnenkwam.
              </span>
            </label>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setMarkPaidRow(null)}
                disabled={!!busyId}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={confirmMarkPaid}
                disabled={!!busyId || !markPaidDate}
                className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-emerald-700 disabled:opacity-50"
              >
                {busyId ? "Bezig..." : "Bevestig"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AdminPartnerFacturenPage;
